import { useState } from 'react'
import { pragueClock, pragueDateLabel } from '../lib/i18n.js'
import { useFold } from '../lib/useFold.js'
import {
  IconCalendar,
  IconCompass,
  IconHeading,
  IconLayers,
  IconLocate,
  IconMinus,
  IconPlus,
  IconSearch,
} from './Icons.jsx'

/** Top-right: map layers, and a compass whenever the map isn't north-up. */
/** Top right: search (phones keep it here instead of in the sheet), layers, compass. */
export function TopControls({ t, layersOpen, onToggleLayers, onSearch, bearing, headingUp, onResetNorth }) {
  const rotated = Math.abs(bearing) > 0.5 || headingUp
  return (
    <div className="ctrls ctrls--top">
      <div className="ctrls__row">
        {onSearch && (
          <button type="button" className="fab" aria-label={t('searchPlaceholder')} title={t('searchPlaceholder')} onClick={onSearch}>
            <IconSearch />
          </button>
        )}
        <button
          type="button"
          className={`fab ${layersOpen ? 'is-active' : ''}`}
          aria-label={t('layersButton')}
          title={t('layersButton')}
          aria-expanded={layersOpen}
          aria-haspopup="dialog"
          data-layers-toggle
          onClick={onToggleLayers}
        >
          <IconLayers />
        </button>
      </div>
      {rotated && (
        <button type="button" className="fab fab--compass" aria-label={t('resetNorth')} title={t('resetNorth')} onClick={onResetNorth}>
          <IconCompass style={{ transform: `rotate(${-bearing}deg)` }} />
        </button>
      )}
    </div>
  )
}

const LOC_LABEL = {
  off: 'locOff',
  locating: 'locLocating',
  live: 'locLive',
  heading: 'locHeading',
  suspended: 'locSuspended',
}

/** Bottom-right: zoom (mouse/trackpad only) and the locate button. */
export function BottomControls({ t, finePointer, onZoomIn, onZoomOut, locateState, headingAvailable, onLocate }) {
  const label = t(locateState === 'live' && headingAvailable ? 'locLiveHeading' : LOC_LABEL[locateState])
  const live = locateState === 'live' || locateState === 'heading'
  return (
    <div className="ctrls ctrls--bottom">
      {finePointer && (
        <div className="fab-group" role="group">
          <button type="button" className="fab fab--flat" aria-label={t('zoomIn')} title={t('zoomIn')} onClick={onZoomIn}>
            <IconPlus />
          </button>
          <button type="button" className="fab fab--flat" aria-label={t('zoomOut')} title={t('zoomOut')} onClick={onZoomOut}>
            <IconMinus />
          </button>
        </div>
      )}
      <button
        type="button"
        className={`fab locate locate--${locateState}`}
        aria-label={label}
        title={label}
        aria-pressed={locateState !== 'off'}
        onClick={onLocate}
      >
        {locateState === 'heading' ? <IconHeading /> : <IconLocate />}
        {live && (
          <span className="locate__badge" aria-hidden="true">
            {t('live')}
          </span>
        )}
      </button>
    </div>
  )
}

/**
 * Exceptional state only: still loading, a planned (non-live) time, or a public
 * holiday. The holiday chip folds to its icon together with the legend; a tap
 * brings the label back, a second one the whole sentence.
 */
export function StatusChips({ t, lang, loading, isOverride, effectiveDate, onResetNow, holiday }) {
  const [holidayOpen, setHolidayOpen] = useState(false)
  const [holidayFolded, showHoliday] = useFold(holiday, loading)
  const sentence = holidayOpen && !holidayFolded
  if (!loading && !isOverride && !holiday) return null
  return (
    <div className="status-chips">
      {loading && (
        <p className="status-chip" role="status">
          <span className="spinner spinner--sm" aria-hidden="true" />
          <span className="status-chip__text">{t('loading')}</span>
        </p>
      )}
      {isOverride && (
        <button type="button" className="status-chip is-accent" onClick={onResetNow} title={t('resetToNow')}>
          <IconCalendar className="status-chip__icon" />
          <span className="status-chip__text">
            {t('overrideChip', `${pragueDateLabel(effectiveDate, lang)} ${pragueClock(effectiveDate)}`)}
          </span>
          <span className="status-chip__action">{t('resetToNow')}</span>
        </button>
      )}
      {holiday && (
        <button
          type="button"
          className={`status-chip is-warn ${sentence ? 'is-open' : ''} ${holidayFolded ? 'is-folded' : ''}`}
          aria-expanded={sentence}
          onClick={() => {
            setHolidayOpen(!holidayFolded && !holidayOpen)
            showHoliday()
          }}
        >
          <IconCalendar className="status-chip__icon" />
          <span className="status-chip__fold">
            <span className="status-chip__text">{sentence ? t('holidayToday', holiday) : t('holidayChip')}</span>
          </span>
        </button>
      )}
    </div>
  )
}
